import { awaitPresetAllowed } from './access.js';
import { presetActionReceipt } from './analytics.js';
/**
 * Encoder-owned native preset command/result helpers used by Save, Select,
 * and joystick. Not a generic observer framework.
 *
 * Per rule: wire-contract-changes-must-not-break-old-firmware (ai.knowledge/global) —
 * old peers without the operation capability retain the absolute-only path.
 */
import type { NativeTarget, Preset } from '@studio/models/control/preset';
import type { Device } from '@studio/models/device';
import { generateId } from '@studio/models';
import { Protocol, toProtocol } from '@studio/models/control';
import type { PTZ } from '@studio/models/ptz';
import {
  CommandDeviceNativePresetAction,
  StatusDeviceNativePresetMode
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { StudioDeviceControlPresetNativeTarget } from '@tryiris-ai/common.protojs/studio/v1/studio';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { T_Maybe } from '@tryiris-ai/js-types';
import * as presetStorage from '#~/api/storage/index.js';
import { locateProxy } from '#~/agent/index.js';
import type { Proxy } from '#~/agent/index.js';
import { moveTo } from '#~/control/movement/commands/index.js';
import { StudioError } from '#~/error/index.js';
import { logger } from '#~/logger/index.js';
import { jsNativeTargetToPb, pbNativeTargetToJs } from '#~/protobuf/preset-target.js';
import type { Group } from '@studio/models/control/preset';
export { jsNativeTargetToPb, pbNativeTargetToJs } from '#~/protobuf/preset-target.js';

/** The encoder's exact refusal when a native SET is withheld before persistence. */
export const NATIVE_PERSISTENCE_WITHHELD =
  'native preset persistence is not ready; absolute presets remain available';

export const NATIVE_PRESET_RESULT_TIMEOUT_MS = 8_000;

/** Tests shorten this so unknown-completion cases do not sleep the full 8s. */
export const nativePresetWait = { timeoutMs: NATIVE_PRESET_RESULT_TIMEOUT_MS };

let generation = 1;

export const nextNativePresetGeneration = (): number => {
  generation += 1;
  return generation;
};

const deviceSaves = new Map<Device['id'], Promise<unknown>>();

export const serializeDevicePresetSave = async <T>(
  deviceId: Device['id'],
  work: () => Promise<T>
): Promise<T> => {
  const previous = deviceSaves.get(deviceId) ?? Promise.resolve();
  const current = previous.catch(() => undefined).then(work);
  const settled = current.then(
    () => undefined,
    () => undefined
  );
  deviceSaves.set(deviceId, settled);
  void settled.then(() => {
    if (deviceSaves.get(deviceId) === settled) deviceSaves.delete(deviceId);
  });
  return current;
};

export const occupiedNativeTargets = (
  proxy: Proxy,
  deviceId: Device['id']
): DeepPartial<StudioDeviceControlPresetNativeTarget>[] => {
  const items = proxy.state.control?.studio?.device?.[deviceId]?.control?.presets?.items ?? {};
  return Object.values(items)
    .map((item) => jsNativeTargetToPb(pbNativeTargetToJs(item?.nativeTarget)))
    .filter((target): target is DeepPartial<StudioDeviceControlPresetNativeTarget> =>
      Boolean(target)
    );
};

export type NativePresetResultView = {
  operationId?: string | undefined;
  position?: T_Maybe<PTZ>;
  requiredAxes?: T_Maybe<number>;
  generation: number;
  studioId: string;
  mode: StatusDeviceNativePresetMode;
  completed: boolean;
  nativeCalls: number;
  target?: T_Maybe<NativeTarget>;
  failure?: T_Maybe<string>;
  supportedReset: boolean;
};

/** Identity of one command; a receipt counts only when every field matches. */
export type NativePresetExpectation = {
  studioId: string;
  action: CommandDeviceNativePresetAction;
  operationId: string;
};

// Renderer code: a plain timer, no Node-only unref.
const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const readNativePresetResult = (
  proxy: Proxy,
  deviceId: Device['id'],
  generation: number,
  expected: NativePresetExpectation
): T_Maybe<NativePresetResultView> => {
  const pb = proxy.state.status?.device?.[deviceId]?.nativePreset;
  if (
    !pb ||
    pb.generation !== generation ||
    pb.studioId !== expected.studioId ||
    pb.operationId !== expected.operationId ||
    pb.action !== expected.action ||
    pb.deviceId !== deviceId
  ) {
    return undefined;
  }
  return {
    operationId: pb.operationId,
    position: validatedPosition(pb.position, pb.requiredAxes),
    requiredAxes: pb.requiredAxes,
    generation: pb.generation ?? 0,
    studioId: pb.studioId ?? '',
    mode: pb.mode ?? StatusDeviceNativePresetMode.NATIVE_PRESET_MODE_UNRESOLVED,
    completed: Boolean(pb.completed),
    nativeCalls: pb.nativeCalls ?? 0,
    target: pbNativeTargetToJs(pb.target),
    failure: pb.failure,
    supportedReset: Boolean(pb.supportedReset)
  };
};

// Status is polled rather than subscribed: the receipt is one field on a state
// tree that changes on every piston tick, and the wait is bounded.
export const waitNativePresetResult = async (
  proxy: Proxy,
  deviceId: Device['id'],
  generation: number,
  expected: NativePresetExpectation,
  timeoutMs = nativePresetWait.timeoutMs
): Promise<T_Maybe<NativePresetResultView>> => {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const result = readNativePresetResult(proxy, deviceId, generation, expected);
    if (result) {
      return result;
    }
    if (Date.now() >= deadline) {
      return undefined;
    }
    await sleep(25);
  }
};

export const commandNativePreset = (
  deviceId: Device['id'],
  action: CommandDeviceNativePresetAction,
  args: {
    studioId: string;
    generation: number;
    operationId: string;
    nativePersistenceReady?: boolean;
    position?: T_Maybe<PTZ>;
    requiredAxes?: T_Maybe<number>;
    name?: string | undefined;
    occupied?: DeepPartial<StudioDeviceControlPresetNativeTarget>[];
    target?: T_Maybe<NativeTarget>;
  }
) => ({
  command: {
    device: {
      nativePreset: {
        id: deviceId,
        action,
        studioId: args.studioId,
        generation: args.generation,
        operationId: args.operationId,
        nativePersistenceReady: args.nativePersistenceReady,
        position: args.position,
        requiredAxes: args.requiredAxes,
        name: args.name,
        occupied: args.occupied ?? [],
        target: jsNativeTargetToPb(args.target)
      }
    }
  }
});

// Known non-native drivers keep their existing absolute-only save/recall path.
export const usesNativeOwner = (proxy: Proxy, deviceId: Device['id']): boolean => {
  const uri = proxy.state.control?.studio?.device?.[deviceId]?.control?.uri;
  const protocol = toProtocol(uri?.split('://')[0]);
  return (
    proxy.state.status?.device?.[deviceId]?.capabilities?.presetOperations === true &&
    (protocol === Protocol.VISCA_IP ||
      protocol === Protocol.VISCA_SERIAL ||
      protocol === Protocol.ONVIF)
  );
};

// Save qualification can perform a native SET only when the encoder consumes
// the persistence guard. Explicit-pose updates share this effective selection.
export const usesNativeSaveOwner = (proxy: Proxy, deviceId: Device['id']): boolean =>
  usesNativeOwner(proxy, deviceId) &&
  proxy.state.status?.device?.[deviceId]?.capabilities?.nativePersistenceGuard === true;

const validatedPosition = (
  position: T_Maybe<{ pan?: T_Maybe<number>; tilt?: T_Maybe<number>; zoom?: T_Maybe<number> }>,
  requiredAxes = 7
): T_Maybe<PTZ> => {
  if (!position || !requiredAxes) return undefined;
  const axes = ['pan', 'tilt', 'zoom'] as const;
  if (
    axes.some(
      (axis, i) =>
        ((requiredAxes & (1 << i)) !== 0 && position[axis] == null) ||
        (position[axis] != null && !Number.isFinite(position[axis]))
    )
  )
    return undefined;
  // Disabled axes are placeholders only; enabled axes never default to zero.
  return Object.freeze({
    pan: position.pan ?? 0,
    tilt: position.tilt ?? 0,
    zoom: position.zoom ?? 0
  });
};

export const persistPresetMapping = async (
  proxy: Proxy,
  deviceId: Device['id'],
  presetId: Preset['id'],
  mutation: {
    group?: string | undefined;
    name?: string | undefined;
    rank?: number | undefined;
    position?: T_Maybe<PTZ>;
    requiredAxes?: T_Maybe<number>;
    nativeTarget?: T_Maybe<NativeTarget>;
    thumbnail?: string | undefined;
  }
): Promise<void> => {
  const item: Record<string, unknown> = {};
  if (mutation.group !== undefined) {
    item.group = mutation.group;
  }
  if (mutation.name !== undefined) {
    item.name = mutation.name;
  }
  if (mutation.rank !== undefined) {
    item.rank = mutation.rank;
  }
  if (mutation.thumbnail !== undefined) {
    item.thumbnail = mutation.thumbnail;
  }
  if (mutation.nativeTarget) {
    item.nativeTarget = jsNativeTargetToPb(mutation.nativeTarget);
  }
  if (mutation.requiredAxes !== undefined) item.requiredAxes = mutation.requiredAxes;
  if (mutation.position) {
    item.position = {
      pan: mutation.position.pan,
      tilt: mutation.position.tilt,
      zoom: mutation.position.zoom
    };
  }
  await proxy.mutate(
    {
      control: {
        studio: {
          device: {
            [deviceId]: {
              control: {
                presets: {
                  items: {
                    [presetId]: item
                  }
                }
              }
            }
          }
        }
      }
    },
    mutation.position || mutation.nativeTarget
      ? {
          control: {
            studio: {
              device: {
                [deviceId]: {
                  control: {
                    presets: {
                      items: {
                        [presetId]: {
                          ...(mutation.position ? { nativeTarget: {} } : { position: {} })
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      : {}
  );
};

// Keep action identity and completed SET evidence through storage failure and
// tab reload. An unresolved action never silently becomes another camera SET.
const pendingKey = (deviceId: string, presetId: string): string =>
  `iris:preset-save:${deviceId}:${presetId}`;
export const pendingPresetId = (
  deviceId: Device['id'],
  group: Group['id'],
  name: string,
  rank: number
): string => {
  const key = `iris:preset-add:${JSON.stringify([deviceId, group, name, rank])}`;
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const id = generateId();
  sessionStorage.setItem(key, id);
  return id;
};
/** Journal entry for one deliberate camera SET whose outcome may still be unknown. */
type PendingSave = NativePresetExpectation & {
  agentId: string;
  uri?: string | undefined;
  identity?: string | undefined;
  generation?: number | undefined;
  startedAt?: number | undefined;
  result?: NativePresetResultView | undefined;
};

export const finishPresetSave = (deviceId: Device['id'], presetId: Preset['id']): void => {
  sessionStorage.removeItem(pendingKey(deviceId, presetId));
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (key?.startsWith('iris:preset-add:') && sessionStorage.getItem(key) === presetId)
      sessionStorage.removeItem(key);
  }
};

// Bind an action to the logical camera before its first await. Preset edits
// themselves are deliberately outside this identity so a successful store does
// not invalidate its own thumbnail/save continuation.
const presetDeviceIdentity = (proxy: Proxy, deviceId: Device['id']): string => {
  const device = proxy.state.control?.studio?.device?.[deviceId];
  return JSON.stringify({
    agent: proxy.id,
    uri: device?.control?.uri,
    model: device?.model == null ? undefined : Array.from(device.model),
    inverted: device?.control?.invert,
    movement: proxy.state.control?.device?.[deviceId]?.movement?.invert
  });
};

// A save may span storage and thumbnail awaits. Connection lifecycle is local
// action evidence, never durable mapping identity or a persisted generation.
const presetConnections = new WeakMap<Proxy, { generation: number }>();
const presetConnection = (proxy: Proxy): { generation: number } => {
  let connection = presetConnections.get(proxy);
  if (!connection) {
    connection = { generation: 0 };
    presetConnections.set(proxy, connection);
    const changed = () => {
      connection!.generation += 1;
    };
    proxy.on?.('state.started', changed);
    proxy.on?.('state.hardReset', changed);
    proxy.on?.('protocol.changed', changed);
    proxy.on?.('stopped', changed);
  }
  return connection;
};

/** Binds the in-flight action to one concrete camera and connection. */
export const freezePresetDevice = (proxy: Proxy, deviceId: Device['id']): (() => void) => {
  const identity = presetDeviceIdentity(proxy, deviceId);
  const connection = presetConnection(proxy);
  const generation = connection.generation;
  return () => {
    if (
      locateProxy(deviceId) !== proxy ||
      presetDeviceIdentity(proxy, deviceId) !== identity ||
      connection.generation !== generation
    )
      throw new StudioError('Preset device changed; re-save the preset.');
  };
};

const journalResult = (key: string, entry: PendingSave, result: NativePresetResultView): void => {
  sessionStorage.setItem(
    key,
    JSON.stringify({ ...entry, result }, (_key, value) =>
      value instanceof Uint8Array ? Array.from(value) : value
    )
  );
};

/**
 * Resolve a journaled SET whose receipt never arrived. Evidence, in order: the
 * correlated receipt is now on status; the mapping already reached storage; or
 * nothing within the result deadline, in which case the entry expires and the
 * caller may save again. Returns the resolved save, or undefined to proceed.
 */
const reconcilePendingSave = async (
  proxy: Proxy,
  deviceId: Device['id'],
  presetId: Preset['id'],
  key: string,
  pending: PendingSave
): Promise<T_Maybe<{ result: NativePresetResultView; position: T_Maybe<PTZ> }>> => {
  const expected: NativePresetExpectation = {
    studioId: pending.studioId,
    operationId: pending.operationId,
    action: pending.action
  };
  const entry: PendingSave = { ...pending, ...expected };
  // A receipt may still land inside the original deadline (e.g. after a tab reload).
  const remaining = Math.max(0, (pending.startedAt ?? 0) + nativePresetWait.timeoutMs - Date.now());
  const live =
    pending.generation == null
      ? undefined
      : await waitNativePresetResult(proxy, deviceId, pending.generation, expected, remaining);
  if (live?.completed) {
    journalResult(key, entry, live);
    return { result: live, position: live.position };
  }
  // The mapping is already persisted: the earlier save got past the camera SET.
  const stored = pbNativeTargetToJs(
    proxy.state.control?.studio?.device?.[deviceId]?.control?.presets?.items?.[presetId]
      ?.nativeTarget
  );
  if (stored) {
    const result: NativePresetResultView = {
      operationId: pending.operationId,
      generation: pending.generation ?? 0,
      studioId: pending.studioId,
      mode: StatusDeviceNativePresetMode.NATIVE_PRESET_MODE_NATIVE,
      completed: true,
      nativeCalls: 1,
      target: stored,
      supportedReset: false
    };
    journalResult(key, entry, result);
    return { result, position: undefined };
  }
  logger.warning(
    `Expiring unconfirmed camera preset save for ${presetId}: ${
      live?.failure ?? 'no receipt within the deadline'
    }`
  );
  sessionStorage.removeItem(key);
  return undefined;
};

export const runNativePresetSave = async (
  deviceId: Device['id'],
  presetId: Preset['id'],
  name: string,
  reuse?: T_Maybe<NativeTarget>,
  token = ''
): Promise<{
  result: T_Maybe<NativePresetResultView>;
  position: T_Maybe<PTZ>;
}> => {
  const proxy = locateProxy(deviceId);
  if (!proxy) {
    throw new Error('NP-914');
  }
  const assertDevice = freezePresetDevice(proxy, deviceId);
  const identity = presetDeviceIdentity(proxy, deviceId);
  if (!usesNativeSaveOwner(proxy, deviceId)) {
    const status = proxy.state.status?.device?.[deviceId];
    const position = status?.movement?.position;
    const axes = ['pan', 'tilt', 'zoom'] as const;
    // Sample invariant: on the legacy position path the snapshot itself is the
    // qualification. An axis is required only when this snapshot carries a value
    // for it; `absoluteMove` describes what the camera can be commanded to do,
    // not what has been measured. `validatedPosition` refuses a wholly unmeasured
    // pose and any present non-finite value, and defaults the absent axes.
    const required = axes.reduce(
      (mask, axis, i) => (position?.[axis] != null ? mask | (1 << i) : mask),
      0
    );
    return { result: undefined, position: validatedPosition(position, required) };
  }
  const key = pendingKey(deviceId, presetId);
  const uri = proxy.state.control?.studio?.device?.[deviceId]?.control?.uri;
  const previous = sessionStorage.getItem(key);
  if (previous) {
    const pending = JSON.parse(previous) as PendingSave;
    if (pending.agentId !== proxy.id || pending.uri !== uri || pending.identity !== identity)
      throw new StudioError('Preset device changed; re-save the preset.');
    if (pending.result?.completed) {
      return { result: pending.result, position: pending.result.position };
    }
    // Unknown completion: resolve it from live evidence, or expire the entry so
    // one lost receipt can never wedge every later save of this shot.
    const reconciled = await reconcilePendingSave(proxy, deviceId, presetId, key, pending);
    assertDevice();
    if (reconciled) return reconciled;
  }
  const occupied = occupiedNativeTargets(proxy, deviceId).filter((item) => {
    if (!reuse) {
      return true;
    }
    if (reuse.kind === 'visca_slot') {
      return item.viscaSlot !== reuse.slot;
    }
    return item.onvifToken !== reuse.token;
  });

  // One guarded SAVE round-trip. nativePersistenceReady=false is the encoder's
  // contract for "resolve the mode but issue no camera SET", so qualification
  // never moves the camera and never writes anything.
  const attempt = async (
    nativePersistenceReady: boolean
  ): Promise<T_Maybe<NativePresetResultView>> => {
    const generation = nextNativePresetGeneration();
    const expected = {
      studioId: presetId,
      operationId: generateId(),
      action: CommandDeviceNativePresetAction.NATIVE_PRESET_ACTION_SAVE
    };
    if (nativePersistenceReady) {
      // Only the deliberate SET attempt is journaled: it is the one that can
      // leave the camera changed with an unknown outcome. Generation and start
      // time let a later save reconcile or expire the entry.
      const entry: PendingSave = {
        agentId: proxy.id,
        uri,
        identity,
        generation,
        startedAt: Date.now(),
        ...expected
      };
      sessionStorage.setItem(key, JSON.stringify(entry));
    }
    try {
      await proxy.mutate(
        commandNativePreset(deviceId, CommandDeviceNativePresetAction.NATIVE_PRESET_ACTION_SAVE, {
          ...expected,
          generation,
          nativePersistenceReady,
          name,
          occupied,
          target: reuse
        }),
        {}
      );
    } catch {
      // Eval may reject; only the full correlated receipt confirms the result.
    }
    assertDevice();
    const received = await waitNativePresetResult(proxy, deviceId, generation, expected);
    if (nativePersistenceReady && received?.completed) {
      journalResult(key, { agentId: proxy.id, uri, identity, generation, ...expected }, received);
    }
    return received;
  };

  // A usable live absolute capture must never depend on the native cloud. Qualify
  // first with SET withheld; an ABS camera completes here and no readiness call
  // is made at all, so a rejecting or unreachable readiness endpoint cannot take
  // away a capture the camera could perform.
  const qualified = await attempt(false);
  assertDevice();
  if (qualified?.completed) {
    if (
      qualified.mode === StatusDeviceNativePresetMode.NATIVE_PRESET_MODE_ABSOLUTE &&
      !qualified.position
    ) {
      throw new StudioError('Unable to save preset without a complete qualified position.');
    }
    return { result: qualified, position: qualified.position };
  }
  const withheld =
    qualified?.nativeCalls === 0 &&
    qualified.failure === NATIVE_PERSISTENCE_WITHHELD &&
    qualified.mode === StatusDeviceNativePresetMode.NATIVE_PRESET_MODE_NATIVE;
  if (!withheld) {
    // Not the readiness gate: a genuine qualification failure, and no SET was sent.
    throw new StudioError(qualified?.failure ?? 'Unable to confirm preset save.');
  }

  // Native fallback only: now the storage authority must prove it can preserve
  // the record before the camera is deliberately changed.
  const persistenceReady = await presetStorage.nativePresetPersistenceReady(proxy.id, token);
  assertDevice();
  if (!persistenceReady) {
    throw new StudioError(NATIVE_PERSISTENCE_WITHHELD);
  }
  const result = await attempt(true);
  assertDevice();
  if (!result?.completed) {
    // The consuming encoder guard proves a withheld refusal occurred before SET.
    // Unknown outcomes stay journaled; a definite readiness refusal can retry.
    if (result?.nativeCalls === 0 && result.failure === NATIVE_PERSISTENCE_WITHHELD) {
      sessionStorage.removeItem(key);
    }
    throw new StudioError(result?.failure ?? 'Unable to confirm preset save.');
  }
  return { result, position: result.position };
};

export const selectStoredPreset = async (deviceId: Device['id'], preset: Preset): Promise<void> => {
  // usesNativeOwner below reads capabilities, so wait until they are published.
  await awaitPresetAllowed(deviceId);
  const proxy = locateProxy(deviceId);
  if (!proxy) throw new Error('NP-220');
  const accepted = presetActionReceipt(deviceId, 'recall');
  // Only an encoder that owns preset operations takes the native path. A native
  // target alone is not enough: an older/downgraded encoder ignores the command,
  // so the record's absolute pose is used instead (or the user must re-save).
  if (usesNativeOwner(proxy, deviceId)) {
    const generation = nextNativePresetGeneration();
    const expected: NativePresetExpectation = {
      studioId: preset.id,
      operationId: generateId(),
      action: CommandDeviceNativePresetAction.NATIVE_PRESET_ACTION_RECALL
    };
    // The mutation acknowledges submission; the correlated receipt carries the
    // outcome. A missing or failed receipt is surfaced, never silently resolved.
    await proxy.mutate(
      commandNativePreset(deviceId, expected.action, {
        ...expected,
        generation,
        target: preset.nativeTarget,
        position: preset.position,
        requiredAxes: preset.requiredAxes
      }),
      {}
    );
    const result = await waitNativePresetResult(proxy, deviceId, generation, expected);
    if (!result) throw new StudioError('Unable to confirm preset recall.');
    if (!result.completed) throw new StudioError(result.failure ?? 'Unable to recall preset.');
    accepted();
    return;
  }
  if (!preset.position) {
    throw new StudioError(
      preset.nativeTarget
        ? 'This camera cannot recall a native preset here; re-save the preset.'
        : 'Re-save this preset before recalling it.'
    );
  }
  // moveTo returns true once the move command is submitted (false only without a
  // proxy), so the receipt fires on every successful absolute recall.
  const submitted = await moveTo(deviceId, preset.position, false, { toolUsed: 'preset' });
  if (submitted !== true) throw new StudioError('Unable to recall preset.');
  accepted();
};

export const resetOwnedNativeTarget = async (
  deviceId: Device['id'],
  target: NativeTarget
): Promise<'reset' | 'unsupported'> => {
  const proxy = locateProxy(deviceId);
  if (!proxy) {
    throw new Error('NP-230');
  }
  const generation = nextNativePresetGeneration();
  const expected = {
    studioId: '',
    operationId: generateId(),
    action: CommandDeviceNativePresetAction.NATIVE_PRESET_ACTION_RESET
  };
  try {
    await proxy.mutate(
      commandNativePreset(deviceId, CommandDeviceNativePresetAction.NATIVE_PRESET_ACTION_RESET, {
        ...expected,
        generation,
        target
      }),
      {}
    );
  } catch {
    // Failure is classified from status, including unsupported reset.
  }
  const result = await waitNativePresetResult(proxy, deviceId, generation, expected);
  if (!result) {
    throw new StudioError('Unable to confirm native preset reset.');
  }
  if (result.completed) {
    return 'reset';
  }
  if (!result.supportedReset && result.failure === 'native preset: reset unsupported') {
    return 'unsupported';
  }
  throw new StudioError(result.failure ?? 'Unable to reset native preset.');
};
